// "You might've seen him before." — grid of press/TV logos. Quiet wall of
// receipts. Logos whose `url` is set become hyperlinks to the appearance
// record (article, clip, archive page, etc.) and pick up a subtle ↗ glyph.
// Entries without a url render as static tiles — same visual, non-clickable.
//
// Logo data lives in ../data/pressLogos.js — shared with the /press kit
// so adding a logo updates both surfaces in one edit.

import PanBar from "../components/PanBar";
import { PRESS_LOGOS as LOGOS } from "../data/pressLogos";

// Single unified tile. Dark navy, warm-cream hairline border.
const TILE = { bg: "#0a1c30", border: "rgba(255,218,173,0.18)" };

export default function TheProof() {
  return (
    <section
      id="proof"
      style={{
        background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(252,179,31,0.1) 0%, transparent 60%), #133862",
        padding: "5rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <PanBar width={72} height={3} margin="0 auto 1.1rem" />
        <h2 style={{
          margin: "0 0 2.4rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.8rem, 4.5vw, 2.8rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}>
          You might've seen us before.
        </h2>

        {/* Flex-wrap with justify-center so odd-count rows center cleanly.
            Fixed tile width (not 1fr stretch) keeps every logo consistent. */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "1.1rem",
        }}>
          {LOGOS.map((logo) => {
            const isLink = Boolean(logo.url);
            // Use an <a> when we have a URL (opens the appearance in a new
            // tab), otherwise a <div>. Shared style + hover behavior so
            // visitors can't tell the difference between linked and unlinked
            // tiles at rest — only on hover do linked tiles shift + show ↗.
            const Tag = isLink ? "a" : "div";
            const tagProps = isLink
              ? { href: logo.url, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <Tag
                key={logo.name}
                title={isLink ? `${logo.name} — open appearance` : logo.name}
                {...tagProps}
                style={{
                  position: "relative",
                  flex: "0 0 160px",
                  maxWidth: "160px",
                  aspectRatio: "2 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.8rem 1rem",
                  boxSizing: "border-box",
                  background: logo.file ? TILE.bg : "rgba(255,255,255,0.04)",
                  border: logo.file ? `1px solid ${TILE.border}` : "1px dashed rgba(255,218,173,0.25)",
                  borderRadius: "10px",
                  color: "rgba(255,218,173,0.75)",
                  fontFamily: "var(--jop-display)",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  textAlign: "center",
                  textDecoration: "none",
                  boxShadow: logo.file ? "0 3px 10px rgba(0,0,0,0.28)" : "none",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                  cursor: isLink ? "pointer" : "default",
                }}
                onMouseEnter={(e) => {
                  if (logo.file) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.32), 0 0 0 2px rgba(239,83,126,0.45)";
                    e.currentTarget.style.borderColor = "transparent";
                  } else {
                    e.currentTarget.style.borderColor = "rgba(239,83,126,0.55)";
                    e.currentTarget.style.color = "var(--jop-pink)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (logo.file) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.28)";
                    e.currentTarget.style.borderColor = TILE.border;
                  } else {
                    e.currentTarget.style.borderColor = "";
                    e.currentTarget.style.color = "";
                  }
                }}
              >
                {logo.file ? (
                  <img src={`/press/${logo.file}`} alt={logo.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
                ) : (
                  logo.name
                )}
              </Tag>
            );
          })}
        </div>
      </div>
    </section>
  );
}
